import { useContext, useRef, useState } from "react";
import { flexRender } from "@tanstack/react-table";
import ToastContext from "../../context/toastProvider";
import Loading from "../atoms/loading";
import ColumnChooser from "./ColumnChooser";
import HeaderCell from "./HeaderCell";
import Pagination from "./Pagination";
import { exportTableToExcel } from "./exportToExcel";
import { TOOLBAR_BUTTON } from "./styles";

const DataTable = ({ table, resetLayout, loading = false, onRowClick, rowClassName, toolbarStart = null, toolbarEnd = null, exportable = false, exportFileName = "tabela", emptyMessage = "Nenhum registro encontrado." }) => {
    const dragKeyRef = useRef(null);
    const { badToast } = useContext(ToastContext);
    const [exporting, setExporting] = useState(false);
    const rows = table.getRowModel().rows;

    const handleExport = async () => {
        setExporting(true);
        try {
            await exportTableToExcel(table, exportFileName);
        } catch (error) {
            console.error(error);
            badToast?.("Não foi possível exportar a planilha.");
        } finally {
            setExporting(false);
        }
    };

    return (
        <section className="relative text-left max-w-full" aria-busy={loading}>
            <div className="flex flex-wrap items-center justify-between gap-2 px-2 py-1">
                <div className="flex min-w-0 flex-wrap items-center gap-2">{toolbarStart}</div>
                <div className="ml-auto flex items-center gap-2">
                    {toolbarEnd}
                    {exportable ? (
                        <button type="button" className={TOOLBAR_BUTTON} title="Exportar para Excel" onClick={handleExport} disabled={exporting}>
                            {exporting ? "Exportando…" : "Exportar"}
                        </button>
                    ) : null}
                    <ColumnChooser table={table} onReset={resetLayout} />
                </div>
            </div>
            {/* Rolagem nos dois eixos dentro do contêiner: o cabeçalho fica fixo e a paginação, abaixo. */}
            <div className="overflow-auto" style={{ maxHeight: "calc(100vh - var(--table-offset, 190px))" }}>
                <table className="border-collapse text-sm text-laranjinha" style={{ width: table.getTotalSize(), minWidth: "100%", tableLayout: "fixed" }}>
                    <thead className="sticky top-0 z-10 bg-mgs-elevated">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <HeaderCell key={header.id} header={header} table={table} dragKeyRef={dragKeyRef} />
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr>
                                <td colSpan={table.getVisibleLeafColumns().length || 1} className="p-6 text-center text-laranjinha/60">{emptyMessage}</td>
                            </tr>
                        ) : (
                            rows.map((row) => (
                                <tr
                                    key={row.id}
                                    onClick={onRowClick ? (event) => onRowClick(row.original, event) : undefined}
                                    className={`border-b border-primary/10 hover:bg-mgs-elevated ${rowClassName?.(row.original) ?? ""}`}
                                >
                                    {row.getVisibleCells().map((cell) => {
                                        const config = cell.column.columnDef.meta?.config ?? {};
                                        return (
                                            <td key={cell.id} style={{ width: cell.column.getSize() }} className={`truncate px-2 py-1 text-center ${config.cellClassName?.(row.original) ?? ""}`}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            <Pagination table={table} />
            {loading ? (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                    <Loading />
                </div>
            ) : null}
        </section>
    );
};

export default DataTable;
